import { ReactNode, useState } from 'react';
import { DisclosureChevron } from './DisclosureChevron';
import { cn } from '../../../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';

interface CardSectionProps {
  title?: ReactNode;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
  headerActions?: ReactNode;
  noPadding?: boolean;
  className?: string;
  /**
   * Denser header + content padding (`p-4` instead of `p-6`) for
   * information-dense settings pages that stack many sections. Opt-in — existing
   * callers keep the roomier default.
   */
  compact?: boolean;
  /**
   * Render the header as a collapse toggle (chevron + optional inline summary
   * when collapsed), hiding the body until expanded. Opt-in; when false the card
   * renders exactly as before.
   */
  collapsible?: boolean;
  /** Initial open state in uncontrolled collapsible mode. Defaults to open. */
  defaultOpen?: boolean;
  /** Controlled open state (pair with `onOpenChange`). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Inline summary shown beside the title while collapsed (e.g. current state). */
  summary?: ReactNode;
}

export function CardSection({
  title,
  subtitle,
  children,
  actions,
  headerActions,
  noPadding = false,
  className,
  compact = false,
  collapsible = false,
  defaultOpen = true,
  open,
  onOpenChange,
  summary,
}: CardSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };

  const headerPadding = compact ? 'p-4' : undefined;
  const bodyPadding = cn(
    noPadding && 'p-0',
    compact && !noPadding && 'p-4',
    (title || headerActions) && !noPadding && 'pt-0'
  );

  // In collapsible mode the title lives inside the trigger <button>, whose
  // content model is phrasing-level only — so everything is <span> (title +
  // subtitle), never a <div>/<CardTitle>(h3)/<CardDescription>(p), which are
  // invalid as button descendants and confuse assistive tech. The plain-card
  // path keeps the semantic CardTitle heading + CardDescription paragraph.
  const titleBlock = (asHeading: boolean) =>
    asHeading ? (
      <div className="min-w-0 space-y-1.5">
        {title && <CardTitle className="text-lg">{title}</CardTitle>}
        {subtitle && <CardDescription>{subtitle}</CardDescription>}
      </div>
    ) : (
      <span className="flex min-w-0 flex-col gap-1.5 text-start">
        {title && (
          <span className="text-lg font-semibold leading-none tracking-tight">{title}</span>
        )}
        {subtitle && <span className="text-sm text-muted-foreground">{subtitle}</span>}
      </span>
    );

  // Collapsible: the title row is a real <button> toggle so keyboard/AT users can
  // expand it; header actions stay outside the trigger (a button-in-button is
  // invalid and swallows their own clicks).
  //
  // This collapses a whole bordered Card from its own header (chevron in the card
  // header, summary replacing the hidden body). For a lightweight inline
  // disclosure WITHOUT card chrome — an optional/advanced group nested inside
  // other content — use `CollapsibleSection` instead. Two intentionally distinct
  // affordances; don't fork a third.
  if (collapsible && (title || headerActions)) {
    return (
      <Card className={className}>
        <Collapsible open={isOpen} onOpenChange={setOpen}>
          {/* The header stacks below `sm:`. Header actions are frequently a pair
              of labelled buttons, which at 375px are wider than the whole card —
              on one row they squeezed the title to nothing and still overflowed
              the card's right edge. */}
          <CardHeader
            className={cn(
              'flex flex-col items-start gap-2 space-y-0 sm:flex-row sm:items-center sm:justify-between',
              headerPadding
            )}
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full min-w-0 items-center gap-2 text-start sm:flex-1"
                aria-expanded={isOpen}
              >
                <DisclosureChevron open={isOpen} className="text-muted-foreground" />
                {titleBlock(false)}
                {!isOpen && summary && (
                  <span className="ms-1 truncate text-sm font-normal text-muted-foreground">
                    {summary}
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            {headerActions && (
              <div className="w-full sm:w-auto sm:shrink-0">{headerActions}</div>
            )}
          </CardHeader>
          <CollapsibleContent>
            <CardContent className={bodyPadding}>{children}</CardContent>
            {actions && <CardFooter className="px-6 pb-6">{actions}</CardFooter>}
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {(title || headerActions) && (
        /* Stacks below `sm:` for the same reason the collapsible branch above
           does, and now for a lot more callers: `headerActions` is where a
           card's create button belongs (`actions` renders a FOOTER, which
           strands it below the card body), so the in-card empty-state sweep
           moved ~50 more buttons up here. On one row at 375px a labelled
           button squeezes the title to a cramped two lines beside it. */
        <CardHeader
          className={cn(
            'flex flex-col items-start gap-2 space-y-0 sm:flex-row sm:items-center sm:justify-between',
            headerPadding
          )}
        >
          {titleBlock(true)}
          {headerActions && (
            <div className="w-full sm:w-auto sm:shrink-0">{headerActions}</div>
          )}
        </CardHeader>
      )}
      <CardContent className={bodyPadding}>{children}</CardContent>
      {actions && <CardFooter className="px-6 pb-6">{actions}</CardFooter>}
    </Card>
  );
}
